import aa from './search/algolia-insights';
import { getIndexName } from '../../../app/Libraries/Algolia/AlgoliaUtils';
import type Pricing from '../../../app/Models/MillbankAPI/Pricing';
import type { AnalyticsCartItem, EnhancedConversionData } from '../../../app/Types/Analytics';

/**
 * E-commerce Analytics Module.
 * Right now it's just got Algolia insights tracking, but could be extended to other platforms later.
 */
export default class EcommerceAnalytics {
  constructor() {
    this.init();
  }

  /** Testing for Klaviyo events */
  // private debugKlaviyo() {
  //   console.debug('window.klaviyo:', window.klaviyo);
  // }

  /**
   * Whether the user has consented to analytics. Gates Klaviyo and Algolia Insights only:
   * they don't understand Google Consent Mode, so we have to block them ourselves.
   * Google calls (gtag events and Ads conversions) are deliberately NOT gated here. Consent
   * Mode decides how gtag sends them (cookieless while denied), and dropping them would lose
   * the pings Google models from. See partials/analytics.edge.
   */
  public hasConsented(): boolean {
    const consentGiven = window.Termly?.getConsentState().analytics === true;
    return consentGiven;
  }

  public init() {
    if (!this.hasConsented()) {
      console.debug('EcommerceAnalytics: User has not consented to analytics. Tracking is disabled.');
      return;
    }

    console.debug('EcommerceAnalytics: User has consented to analytics. Initializing tracking.');

    // Any additional initialization code can go here

    if (window.CURRENT_USER) {
      // this.debugKlaviyo();
      window.klaviyo?.identify({
        email: window.CURRENT_USER.email,
        full_name: window.CURRENT_USER.full_name,
        // https://developers.klaviyo.com/en/docs/javascript_api
        // TODO: Add any other fields like phone_number, title, and organization,
        // which we don't currently have in the object that the frontend has access to
      });
    }
  }

  /**
   * Track a page view event
   * @param pageType The type of page being viewed (e.g., 'product').
   */
  public async trackPageView(
    pageType: 'product' | (string & {}),
  ): Promise<void> {
    if (!this.hasConsented()) {
      return;
    }

    // Code to track page view event
    if (pageType === 'product') {
      const productId = document.querySelector<HTMLElement>('.pdp-main')?.getAttribute('data-product-id');
      if (!productId) {
        console.warn('EcommerceAnalytics: No product ID found on product page for tracking.');
        return;
      }

      const insightsData = {
        userToken: aa('getUserToken')?.toString(),
        authenticatedUserToken: aa('getAuthenticatedUserToken')?.toString(),
        index: getIndexName('products'),
        eventName: 'Product Viewed',
        objectIDs: [productId],
      };

      await aa('viewedObjectIDs', insightsData);
    }
  }

  /**
   * Track an 'add to cart' event.
   * Accepts an array of items and a single indexName and queryID for the entire event.
   * @param params The parameters for the add to cart event.
   * @param params.items Array of items being added to cart.
   * @param params.cartItems (Optional) The full cart items after the addition, used for Klaviyo tracking.
   * @param params.indexName (Optional) The Algolia index name associated with the event.
   * @param params.queryID (Optional) The Algolia query ID associated with the event.
   */
  public async trackAddToCart(params: {
    items: Array<AnalyticsCartItem>;
    cartItems?: Array<AnalyticsCartItem>;
    indexName?: string;
    queryID?: string | null;
  }) {
    // Track via gtag: https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_to_cart
    try {
      window.gtag('event', 'add_to_cart', {
        currency: 'CAD',
        value: this.calculateValue(params.items),
        items: this.generateGtagItems(params.items),
      });
    } catch (error) {
      console.warn('EcommerceAnalytics: Failed to track "add_to_cart" via gtag.', error);
    }

    if (!this.hasConsented()) {
      return;
    }

    // https://www.algolia.com/doc/api-reference/api-methods/added-to-cart-object-ids-after-search/
    // https://www.algolia.com/doc/api-reference/api-methods/added-to-cart-object-ids/

    // TODO: There's probably a better way to grab the index name, but this works for now
    const indexName = params.indexName
      || document.getElementById('sort-by')?.querySelector<HTMLSelectElement>('.ais-SortBy-select')?.value;
    // Determine queryID once for the whole event from URL or hits widget data attribute
    const queryID = params.queryID
        ?? new URLSearchParams(window.location.search).get('qid')
        ?? document.getElementById('hits')?.querySelector<HTMLElement>('.product-card')?.dataset.queryId
        ?? undefined;

    const insightsData = {
      userToken: aa('getUserToken')?.toString(),
      authenticatedUserToken: aa('getAuthenticatedUserToken')?.toString(),
      eventName: 'Added to Cart',
      index: indexName ?? getIndexName('products'),
      objectIDs: params.items.map((item) => item.id),
      objectData: params.items.map((item) => ({
        quantity: item.quantity,
        // Final price, after any discounts
        price: item.price.discount ?? item.price.base,
        // The amount that was discounted
        discount: item.price.discount ? (item.price.base - item.price.discount) : undefined,
      })),
      value: this.calculateValue(params.items),
      currency: 'CAD',
    };

    if (queryID) {
      // Have query, from a search/archive
      await aa('addedToCartObjectIDsAfterSearch', {
        ...insightsData,
        queryID,
      });
    } else {
      await aa('addedToCartObjectIDs', insightsData);
    }

    // Track via Klaviyo: https://developers.klaviyo.com/en/docs/guide_to_integrating_a_platform_without_a_pre_built_klaviyo_integration#added-to-cart
    const addedItem = params.items[0];
    if (addedItem) {
      try {
        const fullCart = params.cartItems ?? params.items;
        // this.debugKlaviyo();
        window.klaviyo?.track('Added to Cart', {
          $value: this.calculateValue(params.items),
          AddedItemProductID: addedItem.id,
          AddedItemProductName: addedItem.name ?? undefined,
          AddedItemSKU: addedItem.sku ?? undefined,
          AddedItemPrice: addedItem.price.discount ?? addedItem.price.base ?? undefined,
          AddedItemQuantity: addedItem.quantity,
          CheckoutURL: `${window.location.origin}/checkout`,
          Items: fullCart.map((item) => ({
            ProductID: item.id,
            ProductName: item.name ?? undefined,
            SKU: item.sku ?? undefined,
            ImageURL: item.imageUrl ?? undefined,
            Quantity: item.quantity,
            ItemPrice: item.price.discount ?? item.price.base ?? undefined,
            RowTotal: (item.price.discount ?? item.price.base ?? 0) * item.quantity,
          })),
        });
      } catch (error) {
        console.warn('EcommerceAnalytics: Failed to track "Added to Cart" via Klaviyo.', error);
      }
    }
  }

  /**
   * Track a 'remove from cart' event.
   * Accepts an array of items
   */
  public async trackRemoveFromCart(params: {
    items: Array<AnalyticsCartItem>;
  }) {
    // Track via gtag: https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_to_cart
    try {
      window.gtag('event', 'remove_from_cart', {
        currency: 'CAD',
        value: this.calculateValue(params.items),
        items: this.generateGtagItems(params.items),
      });
    } catch (error) {
      console.warn('EcommerceAnalytics: Failed to track "remove_from_cart" via gtag.', error);
    }
  }

  /**
   * Track a 'view cart' event.
   */
  public async trackViewCart(params: {
    items: Array<AnalyticsCartItem>;
  }) {
    // Track via gtag: https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_cart
    try {
      window.gtag('event', 'view_cart', {
        currency: 'CAD',
        value: this.calculateValue(params.items),
        items: this.generateGtagItems(params.items),
      });
    } catch (error) {
      console.warn('EcommerceAnalytics: Failed to track "view_cart" via gtag.', error);
    }
  }

  /**
   * Track a 'view item' event.
   */
  // TODO: Call this from the frontend when viewing an item on PDPs
  public async trackViewItem(params: {
    items: Array<AnalyticsCartItem>;
  }) {
    // Track via gtag: https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item
    try {
      window.gtag('event', 'view_item', {
        currency: 'CAD',
        value: this.calculateValue(params.items),
        items: this.generateGtagItems(params.items),
      });
    } catch (error) {
      console.warn('EcommerceAnalytics: Failed to track "view_item" via gtag.', error);
    }

    if (!this.hasConsented()) {
      return;
    }

    // Track via Klaviyo: https://developers.klaviyo.com/en/docs/javascript_api
    // Klaviyo's Viewed Product is always a single item, so we use the first one
    const product = params.items[0];
    if (product) {
      try {
        // this.debugKlaviyo();
        window.klaviyo?.track('Viewed Product', {
          ProductID: product.id,
          ProductName: product.name ?? undefined,
          SKU: product.sku ?? undefined,
          ImageURL: product.imageUrl ?? undefined,
          Price: product.price.discount ?? product.price.base ?? undefined,
          CompareAtPrice: product.price.discount ? product.price.base : undefined,
          Brand: product.brand ?? undefined,
          URL: window.location.href,
        });
        // Populates the "Recently Viewed Items" feed on the customer's Klaviyo profile
        window.klaviyo?.trackViewedItem({
          Title: product.name ?? undefined,
          ItemId: product.id,
          ImageUrl: product.imageUrl ?? undefined,
          Url: window.location.href,
          Metadata: {
            Brand: product.brand ?? undefined,
            Price: product.price.discount ?? product.price.base ?? undefined,
            CompareAtPrice: product.price.discount ? product.price.base : undefined,
          },
        });
      } catch (error) {
        console.warn('EcommerceAnalytics: Failed to track "Viewed Product" via Klaviyo.', error);
      }
    }
  }

  /**
   * Track a 'purchase' event.
   * Accepts an array of items and optionally a queryID.
   * @param params The parameters for the purchase event.
   * @param params.items Array of items being purchased.
   * @param params.queryID (Optional) The Algolia query ID associated with the event.
   */
  public async trackPurchase(params: {
    items: Array<{
      productId: string;
      quantity: number;
      price: Pricing;
    }>;
    queryID?: string | null;
  }) {
    if (!this.hasConsented()) {
      return;
    }

    // https://www.algolia.com/doc/libraries/search-insights/purchased-object-ids-after-search
    // https://www.algolia.com/doc/libraries/search-insights/purchased-object-ids

    // TODO: How would we persist the query ID through to purchase?
    // const queryID = params.queryID
    //   ?? new URLSearchParams(window.location.search).get('qid')
    //   ?? document.getElementById('hits')?.querySelector<HTMLElement>('.product-card')?.dataset.queryId
    //   ?? undefined;

    const insightsData = {
      userToken: aa('getUserToken')?.toString(),
      authenticatedUserToken: aa('getAuthenticatedUserToken')?.toString(),
      eventName: 'Product Purchased',
      index: getIndexName('products'),
      objectIDs: params.items.map((item) => item.productId),
      objectData: params.items.map((item) => ({
        quantity: item.quantity,
        // Final price, after any discounts
        price: item.price.discount ?? item.price.base,
        // The amount that was discounted
        discount: item.price.discount ? (item.price.base - item.price.discount) : undefined,
      })),
      value: params.items.reduce((total, item) => {
        const price = item.price.discount ?? item.price.base;
        return total + price * item.quantity;
      }, 0),
      currency: 'CAD',
    };

    if (params.queryID) {
      await aa('purchasedObjectIDsAfterSearch', {
        ...insightsData,
        queryID: params.queryID,
      });
    } else {
      await aa('purchasedObjectIDs', insightsData);
    }
  }

  /**
   * Track a 'search' event.
   * This is currently called from the search results page, not from the autocomplete (yet).
   * @param searchTerm The search term used.
   * @param resultCount (Optional) The number of results returned.
   */
  public async trackSearch(searchTerm: string, resultCount?: number) {
    // Track via gtag: https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#search
    try {
      window.gtag('event', 'search', {
        search_term: searchTerm,
      });
    } catch (error) {
      console.warn('EcommerceAnalytics: Failed to track "search" via gtag.', error);
    }

    if (!this.hasConsented()) {
      return;
    }

    // Track via Klaviyo: https://developers.klaviyo.com/en/docs/guide_to_setting_up_api_based_website_activity_events#searched-site
    try {
      const klaviyoProperties: Record<string, unknown> = { SearchTerm: searchTerm };
      if (resultCount !== undefined) {
        klaviyoProperties.ReturnedResults = resultCount;
      }
      // this.debugKlaviyo();
      window.klaviyo?.track('Searched Site', klaviyoProperties);
    } catch (error) {
      console.warn('EcommerceAnalytics: Failed to track "Searched Site" via Klaviyo.', error);
    }
  }

  /**
   * Track a 'sign_up' event
   * @param method The method used for sign up (e.g., 'Google', 'Email', etc.)
   * @param userData (Optional) Identity fields for Google Ads enhanced conversions.
   *   Purchase is the main Google Ads conversion (imported from GA4 until wired natively
   *   via the checkout submodule). Registration is tracked as a supporting conversion
   *   because account holders get member pricing and promotions.
   */
  public async trackSignUp(method: string, userData?: EnhancedConversionData) {
    // Both destinations are Google, so neither is gated here: Consent Mode applies
    // analytics_storage to the GA4 event and ad_storage/ad_user_data to the Ads conversion.
    // Track via gtag: https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#sign_up
    try {
      window.gtag('event', 'sign_up', {
        method,
      });
    } catch (error) {
      console.warn('EcommerceAnalytics: Failed to track "sign_up" via gtag.', error);
    }

    this.trackAdsConversion(window.GOOGLE_ADS?.signupLabel, { userData });
  }

  /**
   * Track a 'login' event
   * @param method The method used for login (e.g., 'Google', 'Email', etc.)
   */
  // TODO: Call this from the frontend when a user logs in
  public async trackLogin(method: string) {
    // Track via gtag: https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#login
    try {
      window.gtag('event', 'login', {
        method,
      });
    } catch (error) {
      console.warn('EcommerceAnalytics: Failed to track "login" via gtag.', error);
    }
  }

  /**
   * Fire a Google Ads conversion.
   *
   * This is separate from the GA4 events above. GA4 measures behaviour; this reports a
   * conversion back to the Ads account so bidding can optimise against it. Both ride on
   * the same gtag.js library, told apart by the `send_to` target.
   *
   * No-ops unless GOOGLE_ADS_ID and the matching label are set in the environment, so
   * this stays inert until the ad account exists and the labels are configured. There is no
   * consent check: Consent Mode handles it. While ad_storage/ad_user_data are denied, gtag
   * sends a cookieless ping and drops the user_data, and Google models the conversion.
   *
   * @param label The conversion label from the Google Ads conversion action.
   * @param params.value (Optional) Conversion value. Omit for registrations, which have
   *   no transaction value; Google treats a missing value as the action's default.
   * @param params.currency (Optional) Required by Google whenever a value is sent.
   * @param params.transactionId (Optional) Order ID. Google dedupes repeat conversions
   *   on this, so always pass it for purchases (a customer refreshing the thank-you page
   *   would otherwise be counted twice).
   * @param params.userData (Optional) Identity fields for enhanced conversions matching.
   */
  private trackAdsConversion(
    label: string | undefined,
    params: {
      value?: number;
      currency?: string;
      transactionId?: string;
      userData?: EnhancedConversionData;
    } = {},
  ): void {
    const adsId = window.GOOGLE_ADS?.id;
    if (!adsId || !label) {
      // Ads tracking is not configured for this environment. Expected on staging and local.
      return;
    }

    try {
      if (params.userData) {
        // Set before the conversion event so gtag attaches it to the send below.
        window.gtag('set', 'user_data', params.userData);
      }

      const payload: Record<string, unknown> = { send_to: `${adsId}/${label}` };
      if (params.value !== undefined) {
        payload.value = params.value;
        payload.currency = params.currency ?? 'CAD';
      }
      if (params.transactionId) {
        payload.transaction_id = params.transactionId;
      }

      window.gtag('event', 'conversion', payload);
    } catch (error) {
      console.warn('EcommerceAnalytics: Failed to send Google Ads conversion.', error);
    }
  }

  /**
   * Generate the correct format for gtag items
   * @param items The analytics cart items
   * @returns The formatted items for gtag
   */
  private generateGtagItems(items: Array<AnalyticsCartItem>) {
    return items.map((item) => ({
      item_name: item.name,
      item_id: item.id,
      quantity: item.quantity,
      price: item.price.discount ?? item.price.base,
      discount: item.price.discount ? (item.price.base - item.price.discount) : undefined,
      item_brand: item.brand,
      // TODO: Can we get the full product info and add these in the future?
      // item_category: '', // If category data is available
      // item_category2: '',
      // item_category3: '',
      // item_category4: '',
      // item_category5: '',
      // coupon: '', // If a coupon was used (wait for promo engine, if at all)
      // item_variant: '', // If variant data is available
    }));
  }

  /**
   * Calculate the total value of the cart items
   * @param items The cart items to sum up
   * @returns The total value of the cart items
   */
  private calculateValue(items: Array<AnalyticsCartItem>) {
    return items.reduce((total, item) => {
      const price = item.price.discount ?? item.price.base;
      return total + price * item.quantity;
    }, 0);
  }
};
